import React, { useContext, useState } from "react";
import SectionCard from "../../profiles/SectionCard";
import DetailTile from "./detail_tile";
import MediaOutletReporters from "./media_outlet_reporters";
import { MediaOutletContext } from "../data/media_outlet_context_management";

// Truncation threshold for the description body below, duplicated verbatim from AboutSection's own
// bio renderer (WET before DRY — this file's own DetailTile is the recipe's first duplication too;
// extract a shared helper only once a third consumer needs it, per the codebase's own
// duplicate-twice-before-abstracting rule).
const DESCRIPTION_CHAR_LIMIT = 280;

// Media Outlet content as shared <SectionCard>s: Overview (Website/Domain Authority facts, plus the
// free-text description) and Reporters (who works there now). Contact lives in the page's right
// rail. A curator always sees every card, with neutral empty states, since the outlet has no
// per-section show/hide config to switch anything off; an empty card collapses away for a viewer,
// matching the other profile engines. Reporters owns its own collapse decision (it knows its rows
// only after fetching).
function MediaOutletSections() {
  const { mediaOutlet, viewerContext, setOpenEditModal } = useContext(MediaOutletContext);
  const editable = !!viewerContext?.editable;
  const [expanded, setExpanded] = useState(false);

  const showWebsiteTile = !!mediaOutlet.domain;
  const showDomainAuthorityTile = mediaOutlet.domain_authority != null;
  const hasTiles = showWebsiteTile || showDomainAuthorityTile;
  const hasDescription = !!mediaOutlet.description;

  // Count/slice by code point (not UTF-16 unit) so a boundary never splits an emoji surrogate pair,
  // matching AboutSection's own truncation behaviour exactly (see the module comment above).
  const descriptionChars = mediaOutlet.description ? Array.from(mediaOutlet.description) : [];
  const shouldTruncateDescription = descriptionChars.length > DESCRIPTION_CHAR_LIMIT;
  const collapsedDescription = `${descriptionChars
    .slice(0, DESCRIPTION_CHAR_LIMIT)
    .join("")
    .replace(/[ \t]+$/, "")}…`;

  return (
    <div className="container-fluid px-0">
      {(editable || hasDescription || hasTiles) && (
        <SectionCard
          id="media-outlet-section-overview"
          title="Overview"
          editable={editable}
          onEdit={() => setOpenEditModal("OVERVIEW")}
          editButtonId="modal-button-OVERVIEW"
        >
          {hasTiles && (
            <div className="row">
              {showWebsiteTile && (
                <DetailTile icon="fa-solid fa-globe" label="Website">
                  <a href={`https://${mediaOutlet.domain}`} target="_blank" rel="noopener noreferrer">
                    {mediaOutlet.domain}
                  </a>
                </DetailTile>
              )}

              {showDomainAuthorityTile && (
                <DetailTile icon="fa-solid fa-magnifying-glass-chart" label="Domain Authority">
                  {mediaOutlet.domain_authority}
                </DetailTile>
              )}
            </div>
          )}

          {hasDescription ? (
            <div className={hasTiles ? "border-top pt-3 mt-3" : ""}>
              <div style={{ whiteSpace: "pre-wrap" }} className="text-break">
                {shouldTruncateDescription ? (
                  <>
                    {expanded ? mediaOutlet.description : collapsedDescription}{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline text-primary border-0"
                      style={{ font: "inherit" }}
                      aria-expanded={expanded}
                      onClick={() => setExpanded((prev) => !prev)}
                    >
                      {expanded ? "see less" : "see more"}
                    </button>
                  </>
                ) : (
                  mediaOutlet.description
                )}
              </div>
            </div>
          ) : (
            !hasTiles && <div className="text-muted">No overview added yet.</div>
          )}
        </SectionCard>
      )}

      <MediaOutletReporters />
    </div>
  );
}

export default MediaOutletSections;
